import { Box, Tooltip, type GridProps } from '@mui/material';

import { useAppSelector } from '@/redux/hooks';
import { IngestFile } from '@/redux/slices/slice_types';
import { selectStages } from '@/redux/selectors/constantsSelectors';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { StatusColors } from '@/constants/constants';
import { FileIngest, PolarisIcon, TableStatus } from '@/exports/columnCommonComponents';
import IngestAwaitingFileButton from '@/components/_common/Table/IngestAwaitingFileButton';

export type ActionableTableStatusProps = GridProps & {
  status: string;
  row: IngestFile;
};

const ActionableTableStatus = ({ status, row }: ActionableTableStatusProps) => {
  const stages = useAppSelector(selectStages);
  const allowAutoIngestRegister = useConfigValue(['ingestManagementConfig', 'allowAutoIngestRegister']) || false;
  const fileIngestVersion = useConfigValue(['ingestManagementConfig', 'version']) || 'v1';

  const renderStatus = () => {
    switch (status) {
      case 'awaiting_file':
        return (
          <Box sx={{ ml: 1.5 }}>
            <IngestAwaitingFileButton ingestRecord={row} />
          </Box>
        );
      case 'ready_for_ingest':
        if (fileIngestVersion === 'v2' && row.status !== 'failed' && (!row.auto_ingest || allowAutoIngestRegister)) {
          return (
            <Box sx={{ ml: 1.5, display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
              <FileIngest ingestFile={row} />
              {row.status_reason && (
                <Box sx={{ color: 'error.main', height: 16, ml: 1 }}>
                  <Tooltip title={row.status_reason}>
                    <PolarisIcon icon="error" height={16} />
                  </Tooltip>
                </Box>
              )}
            </Box>
          );
        }
      default:
        return (
          <Box sx={{ p: 2 }}>
            <TableStatus
              status={status}
              statusReason={row.status_reason}
              colorOverride={StatusColors[row.status as keyof typeof StatusColors]}
              customStatuses={stages}
            />
          </Box>
        );
    }
  };

  return renderStatus();
};

export default ActionableTableStatus;
